import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserContext } from './App';
import HSMRenderer, {
  HSMRendererProps,
  HSMView,
  HSMViewProps,
  ToolBar,
} from './HSMRenderer.tsx';
import { updateTitleOfActivity } from './utils/stateUtils.tsx';

export type EditModeButtonProps = {
  inEditMode: boolean;
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>;
};

export const EditModeButton = ({
  inEditMode,
  setInEditMode,
}: EditModeButtonProps) => {
  const editModeIcon = () => {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-2.5 -2.5 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M12.238 5.472L3.2 14.51l-.591 2.016 1.975-.571 9.068-9.068-1.414-1.415zM13.78 3.93l1.414 1.414 1.318-1.318a.5.5 0 0 0 0-.707l-.708-.707a.5.5 0 0 0-.707 0L13.781 3.93zm3.439-2.732l.707.707a2.5 2.5 0 0 1 0 3.535L5.634 17.733l-4.22 1.22a1 1 0 0 1-1.237-1.241l1.248-4.255 12.26-12.26a2.5 2.5 0 0 1 3.535 0z'></path>
      </svg>
    );
  };

  const viewModeIcon = () => {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-2 -6 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M18 6c0-1.81-3.76-3.985-8.007-4C5.775 1.985 2 4.178 2 6c0 1.825 3.754 4.006 7.997 4C14.252 9.994 18 7.82 18 6zm-8 6c-5.042.007-10-2.686-10-6S4.984-.017 10 0c5.016.017 10 2.686 10 6s-4.958 5.993-10 6zm0-2a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'></path>
      </svg>
    );
  };

  const buttonClassName = inEditMode ? 'btn btn-warning' : 'btn btn-primary';
  const buttonText = inEditMode ? 'View mode' : 'Edit mode';
  const icon = inEditMode ? viewModeIcon : editModeIcon;

  return (
    <button
      onClick={() => setInEditMode((prev) => !prev)}
      className={buttonClassName}
    >
      {icon()}
      {buttonText}
    </button>
  );
};

export type SaveChangesProps = {
  savedChangesText: string;
  isUnsavedChanges: boolean;
  onSave: () => void;
};

export const SaveChanges = ({
  savedChangesText,
  isUnsavedChanges,
  onSave,
}: SaveChangesProps) => {
  const SaveIcon = () => {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-3 -3 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M2 0h11.22a2 2 0 0 1 1.345.52l2.78 2.527A2 2 0 0 1 18 4.527V16a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm0 2v14h14V4.527L13.22 2H2zm4 8h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2zm0 2v4h6v-4H6zm7-9a1 1 0 0 1 1 1v3a1 1 0 0 1-2 0V4a1 1 0 0 1 1-1zM5 3h5a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 3h3V5H6v1z'></path>
      </svg>
    );
  };
  if (isUnsavedChanges) {
    const buttonClassName = 'btn btn-success';
    const buttonText = 'Save changes';

    return (
      <button onClick={onSave} className={buttonClassName}>
        <SaveIcon />
        {buttonText}
      </button>
    );
  }

  return <span>{savedChangesText}</span>;
};

export type EditableHSMRendererProps = {
  userContext: UserContext;
  nextUID: () => number;
  setStartingId: (id: number) => void;
  setCursor: React.Dispatch<React.SetStateAction<string>>;
};

export const EditableHSMRenderer = ({
  userContext,
  nextUID,
  setStartingId,
  setCursor,
}: EditableHSMRendererProps) => {
  const [inEditMode, setInEditMode] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [savedChangesText, setSavedChangesText] = useState('');
  const [showingCommentsFor, setShowingCommentsFor] = useState(-1);
  const [showingPapersFor, setShowingPapersFor] = useState(-1);

  const { id } = useParams();
  const [activityProps, setActivityProps] = useState<any>({});
  const [crumbIds, setCrumbIds] = useState([]);
  // const [hsm, setHsm] = useState(0); // the hsm props to be loaded from the db
  const [hsmLoaded, setHsmLoaded] = useState(false);

  // wrapper for hiding papers everytime comments are shown
  function showCommentsWrapper(id: number) {
    setShowingPapersFor(-1);

    if (id === showingCommentsFor) {
      // toggle comments off
      setShowingCommentsFor(-1);
    } else {
      setShowingCommentsFor(id);
    }
  }

  // wrapper for hiding comments everytime papers are shown
  function showPapersWrapper(id: number) {
    setShowingCommentsFor(-1);
    if (id === showingPapersFor) {
      // toggle comments off
      setShowingPapersFor(-1);
    } else {
      setShowingPapersFor(id);
    }
  }

  function onSave() {
    if (activityProps.text === '') {
      setActivityProps(
        updateTitleOfActivity(activityProps, 'Untitled workflow')
      );
    }

    fetch(`http://34.74.45.28:8080/hsm_edit/update/` + id, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(activityProps),
    }).then((res) => {
      if (res.ok) {
        setIsUnsavedChanges(false);
        setSavedChangesText('All changes saved');
      }
    });
  }

  // This method fetches the hsms from the database.
  useEffect(() => {
    async function getHsm() {
      const response = await fetch(`http://34.74.45.28:8080/hsm_edit/` + id);

      if (!response.ok) {
        const message = `An error occured: ${response.statusText}`;
        window.alert(message);
        return;
      }

      const hsmRes = await response.json();

      if (userContext.id != hsmRes.author) {
        // the current user is not the owner of this HSM, so we return
        return;
      }

      setHsmLoaded(true);
      setActivityProps(hsmRes);
      setStartingId(hsmRes.id);
      setCrumbIds([hsmRes.id]);
    }

    getHsm();

    return;
  }, [hsmLoaded]);

  if (!hsmLoaded) {
    return null;
  }

  const EditableHSMRendererToolbar = () => {
    return (
      <ToolBar>
        <EditModeButton inEditMode={inEditMode} setInEditMode={setInEditMode} />
        <SaveChanges
          savedChangesText={savedChangesText}
          isUnsavedChanges={isUnsavedChanges}
          onSave={onSave}
        />
      </ToolBar>
    );
  };

  const human = activityProps.viewMode === 'human';
  const centerComponent = (
    <div className='viewmode'>
      <div className='btn-group'>
        <input
          type='radio'
          className='btn-check'
          name='options'
          id='radio1'
          autoComplete='off'
          checked={human}
          disabled={!human}
        />
        <label className='btn btn-outline-primary' htmlFor='radio1'>
          Human caregiver
        </label>

        <input
          type='radio'
          className='btn-check'
          name='options'
          id='radio2'
          autoComplete='off'
          checked={!human}
          disabled={human}
        />
        <label className='btn btn-outline-primary' htmlFor='radio2'>
          Robot caregiver
        </label>
      </div>
    </div>
  );

  const hsmViewProps: HSMViewProps = {
    activityProps: activityProps,
    setActivityProps: setActivityProps,
    crumbIds: crumbIds,
    setCrumbIds: setCrumbIds,
    discussionButtons: false,
    viewOnlyMode: false,
    editing: inEditMode,
    setCursor: setCursor,
    nextUID: nextUID,
    setShowingCommentsFor: showCommentsWrapper,
    setShowingPapersFor: showPapersWrapper,
    setIsUnsavedChanges: setIsUnsavedChanges,
    isHumanMode: human,
  };

  const hsmRendererProps: HSMRendererProps = {
    userContext: userContext,
    activityId: id,
    activityProps: activityProps,
    setActivityProps: setActivityProps,
    showingCommentsFor: showingCommentsFor,
    setShowingCommentsFor: setShowingCommentsFor,
    showingPapersFor: showingPapersFor,
    setShowingPapersFor: setShowingPapersFor,
    crumbIds: crumbIds,
    setCrumbIds: setCrumbIds,
    setCursor: setCursor,
    toolbar: <EditableHSMRendererToolbar />,
    children: [<HSMView {...hsmViewProps} />],
    editing: true,
    inEditMode: inEditMode,
    setIsUnsavedChanges: setIsUnsavedChanges,
    center: () => centerComponent,
    isHumanMode: human,
  };

  return <HSMRenderer {...hsmRendererProps} />;
};

export default EditableHSMRenderer;
